import { useEffect, useRef } from 'react';
import { type Map as AtlasMap, data as atlasData } from 'azure-maps-control';
import { type BaseAzureMapProps } from './MapWithState';
import { useAuthOptions } from './useAuthOptions';

// Access atlas from global window object (loaded via CDN)
declare global {
  interface Window {
    atlas: typeof import('azure-maps-control');
  }
}

const DEBUG = true; // Set to true to log detailed map events
const log = (...args: any[]) => DEBUG && console.log(...args);
const logError = (...args: any[]) => console.error(...args);

type Props = {
  azureMapsClientId: string;
} & BaseAzureMapProps;

export const Map = ({ azureMapsClientId, features, layers, dataSourceId, height = '300px', width = '100%', mapOptions = {} }: Props) => {

  const mapRef = useRef<AtlasMap | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const authOptions = useAuthOptions(azureMapsClientId);

  useEffect(() => {

    log('[AzureMap] Initializing map', {
      hasContainer: !!containerRef.current,
      featuresCount: features.length,
      layersCount: layers.length,
      dataSourceId,
      clientId: azureMapsClientId
    });

    if (!containerRef.current) {
      logError('[AzureMap] Container ref is null');
      return;
    }

    // Check if atlas is loaded from CDN
    if (typeof window.atlas === 'undefined') {
      logError('[AzureMap] Azure Maps SDK not loaded. Make sure the CDN script is included in your HTML.');
      return;
    }

    const atlas = window.atlas;

    // Initialize map
    log('[AzureMap] Creating map instance with authOptions:', authOptions);

    const map = new atlas.Map(containerRef.current, {
      view: 'Auto',
      authOptions,
      ...mapOptions,
    });

    mapRef.current = map;

    log('[AzureMap] Map instance created, waiting for events');

    map.events.add('ready', () => {
      log('[AzureMap] Map ready event fired - map is ready to use');
      // Load data on ready instead of waiting for load event
      loadData();
    });

    map.events.add('load', () => {
      log('[AzureMap] Map load event fired');
    });

    map.events.add('error', (e) => {
      logError('[AzureMap] Map error:', e);
    });

    map.events.add('idle', () => {
      log('[AzureMap] Map idle event - rendering complete');
    });

    map.events.add('styledata', () => {
      log('[AzureMap] Map styledata event - style loaded');
    });

    function loadData() {
      try {
        log('[AzureMap] Starting loadData function');
        const dataSource = new atlas.source.DataSource(dataSourceId);
        map.sources.add(dataSource);

        log('[AzureMap] Adding layers and features to map');
        log(`[AzureMap] DataSource ID: ${dataSourceId}`);
        log(`[AzureMap] Layers count: ${layers.length}`);
        log(`[AzureMap] Features count: ${features.length}`);

        for (const layer of layers) {
          map.layers.add(layer);
        }

        for (const f of features) {
          dataSource.add(f);
        }

        if (features.length > 0) {
          map.setCamera({
            bounds: atlasData.BoundingBox.fromData(new atlasData.FeatureCollection(features as atlasData.Feature<atlasData.Geometry, unknown>[])),
            padding: 25,
          });
        }
        log('[AzureMap] Data loaded successfully');
      } catch (error) {
        logError('[AzureMap] Error loading data:', error);
      }
    }

    // Cleanup on unmount
    return () => {
      log('[AzureMap] Cleaning up map');
      if (mapRef.current) {
        mapRef.current.dispose();
        mapRef.current = null;
      }
    };
  }, [authOptions, dataSourceId, features, layers, mapOptions, azureMapsClientId]);

  return (
    <div style={{ height, width }} ref={containerRef} />
  );
};